"use client";

import { useState, useCallback } from "react";
import { useTranslations } from "next-intl";
import { Paper, Stack, Group, Text, TbColumns } from "@forestsync/ui";
import { GeoTiffViewer } from "./GeoTiffViewer";

interface ImageryComparisonProps {
  leftUrl: string;
  rightUrl: string;
  leftLabel: string;
  rightLabel: string;
  type: "biomass" | "biomass-change" | "sentinel";
  onLeftLoad?: () => void;
  onRightLoad?: () => void;
  onLeftError?: (error: Error) => void;
  onRightError?: (error: Error) => void;
  className?: string;
}

/**
 * ImageryComparison - Compare two GeoTIFF images side-by-side
 *
 * Features:
 * - Side-by-side view with synchronized zoom/pan
 * - Independent loading states
 * - Labeling for each side
 */
export function ImageryComparison({
  leftUrl,
  rightUrl,
  leftLabel,
  rightLabel,
  type,
  onLeftLoad,
  onRightLoad,
  onLeftError,
  onRightError,
  className = "",
}: ImageryComparisonProps) {
  const t = useTranslations("sink.imagery");
  const [leftLoaded, setLeftLoaded] = useState(false);
  const [rightLoaded, setRightLoaded] = useState(false);

  const handleLeftLoad = useCallback(() => {
    setLeftLoaded(true);
    if (onLeftLoad) onLeftLoad();
  }, [onLeftLoad]);

  const handleRightLoad = useCallback(() => {
    setRightLoaded(true);
    if (onRightLoad) onRightLoad();
  }, [onRightLoad]);

  const bothLoaded = leftLoaded && rightLoaded;

  return (
    <div className={`h-full overflow-hidden ${className}`}>
      <Stack gap="sm" className="h-full flex flex-col">
        {/* Side-by-Side Viewers */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 flex-1 min-h-0">
          {/* Left Viewer */}
          <div className="flex flex-col h-full min-h-0">
            <Paper p="xs" mb="xs" withBorder className="flex-none">
              <Text size="sm" fw={600} className="text-center">
                {leftLabel}
              </Text>
            </Paper>
            <div className="flex-1 min-h-0">
              <GeoTiffViewer
                url={leftUrl}
                type={type}
                onLoad={handleLeftLoad}
                onError={onLeftError}
              />
            </div>
          </div>

          {/* Right Viewer */}
          <div className="flex flex-col h-full min-h-0">
            <Paper p="xs" mb="xs" withBorder className="flex-none">
              <Text size="sm" fw={600} className="text-center">
                {rightLabel}
              </Text>
            </Paper>
            <div className="flex-1 min-h-0">
              <GeoTiffViewer
                url={rightUrl}
                type={type}
                onLoad={handleRightLoad}
                onError={onRightError}
              />
            </div>
          </div>
        </div>

        {/* Comparison Info */}
        {bothLoaded && (
          <Paper p="xs" withBorder className="bg-blue-50 dark:bg-blue-900/20 flex-none">
            <Group gap="xs">
              <TbColumns
                size={18}
                className="text-blue-600 dark:text-blue-400"
              />
              <Text size="xs" c="dimmed">
                {t.rich("viewing", {
                  leftLabel,
                  rightLabel,
                  strong: (chunks) => <strong>{chunks}</strong>,
                })}
              </Text>
            </Group>
          </Paper>
        )}
      </Stack>
    </div>
  );
}
